import { useState, useEffect, useMemo, useCallback } from "react";
import {
  formatReportDate,
  movingSum,
  makeWhole,
  objectifyData,
  refAreaCalc,
} from "../utils/DataConversion";

// fetches data from both files
// optionally, filters the data
// parses vectorized format
// returns single structure that can be used by the app

interface UseDataProps {
  vectorizedData: Data.Vectorized | undefined;
  filter: App.CurrentFilter;
  dateFilter: [number, number] | undefined;
}

export interface DashboardData {
  agg: App.Agg;
  map: App.CountyStats[];
  daily: App.DailyData[];
  demo: App.Demo;
  bedvent: {
    beds: App.ResourceElement[];
    vents: App.ResourceElement[];
  };
  hospital: {
    census: App.HospitalCensusRecord[];
    admissions: App.HospitalRecord[];
  };
  probable: App.ProbableElement;
  variants: App.Variant[];
  variantsByWeek: Data.VariantsByWeek[];
  misc: Data.Misc;
}

interface ReportDate {
  raw: string | null;
  formatted: string | null;
}

export default function useDashboardData(props: UseDataProps) {
  const { vectorizedData, filter: currentFilter, dateFilter } = props;
  const [data, setData] = useState<DashboardData>();
  const [reportDate, setReportDate] = useState<ReportDate>();

  const getIndexes = useCallback(
    (filterIds: string[]) => {
      return vectorizedData!.metrics.data.district.reduce(
        (values: number[], record: string, index: number) => {
          if (filterIds.includes(record)) {
            values.push(index);
          }
          return values;
        },
        []
      );
    },
    [vectorizedData]
  );

  const seriesFromVector = useMemo(() => {
    if (!vectorizedData) return;

    const timeSeriesData = vectorizedData.metrics.data;

    // if there are no filters, than the filter array length will be 0
    const filterIds = currentFilter.filters.length
      ? currentFilter.filters.map((filter) => filter.id)
      : ["0"];

    const indexes = getIndexes(filterIds);

    // this segment is focused on getting regional indexes if one
    // county is selected for bed/vent and hospital census
    let districtIndexes: number[] = [];
    // region id is an indicator that is set by a click on the CountyDist map
    // it uses logic to determine if the counties are in different regions
    // if a single county OR counties are in the same region, it populates
    // the regionId field. If not, it sets it to undefined
    // add countyFilter type just in case :)
    if (currentFilter.regionId) {
      let regionId;
      // by default, use the regionId if it exists on the current filter object
      // see type file for more information on CurrentFilter interface
      if (currentFilter.regionId) {
        regionId = currentFilter.regionId;
      }

      if (regionId) {
        districtIndexes = getIndexes([regionId.toString()]);
      }
    }

    // for county-level filtering & hospitalizations
    // we need to make sure they are NEVER county
    const valueOverrides: Array<keyof Data.Vectors> = [
      "m2b_hospitalized_icu_occupied_covid",
      "m2b_hospitalized_icu_occupied_non_covid",
      "m2b_hospitalized_icu_available",
      "m2b_hospitalized_icu_supply",
      "m2b_hospitalized_vent_occupied_covid",
      "m2b_hospitalized_vent_occupied_non_covid",
      "m2b_hospitalized_vent_available",
      "m2b_hospitalized_vent_supply",
      "m1a_beds_all_occupied_beds_covid_19_pui_smoothed",
      "m1a_beds_all_occupied_beds_covid_19_pts_smoothed",
      "m1a_beds_all_occupied_beds_covid_19_smoothed",
    ];

    function getValueOverride<K extends keyof Data.Vectors>(
      index: number,
      key: K
    ) {
      // get the date record in question so we can find the district date info
      const date = timeSeriesData.date[index];
      // get the index of the district info based on date
      // run a min / max incase the ordering of the vectors changes
      // todo need to make sure this OK
      let districtIndex: number;
      if (districtIndexes.length > 0) {
        districtIndex =
          timeSeriesData.date
            .slice(
              Math.min(...districtIndexes),
              // for some add reason, this helped it capture the entire sequence of indexes
              Math.max(...districtIndexes) + 1
            )
            .findIndex((vectorDate) => vectorDate === date) +
          Math.min(...districtIndexes);
      } else {
        // ? this relies on the state indexes being up front
        districtIndex = timeSeriesData.date.findIndex(
          (vectorDate) => vectorDate === date
        );
      }
      return timeSeriesData[key][districtIndex];
    }

    let objectedData: any[] = [];

    (Object.keys(timeSeriesData) as Array<keyof typeof timeSeriesData>).forEach(
      (key) => {
        (timeSeriesData[key] as Array<string | number | null>).forEach(
          (value, index) => {
            // check to see if the index matches the items in our predefined index set above
            if (indexes.includes(index)) {
              // check to see if the record already exists to (a) add info to or (b) create new
              // this is a pattern kept throughout the useDashboardData hook
              const existingRecord = objectedData.find(
                (record) => record && record.index === index
              );

              if (existingRecord) {
                const existingIndex = objectedData.findIndex(
                  (record) => record.index === existingRecord.index
                );

                objectedData[existingIndex] = {
                  ...existingRecord,
                  [key]:
                    currentFilter.filterType === "county" &&
                    valueOverrides.includes(key)
                      ? getValueOverride(index, key)
                      : value,
                };
              } else {
                objectedData.push({
                  index: index,
                  [key]: value,
                });
              }
            }
          }
        );
      }
    );

    return objectedData as Data.Vector[];
  }, [vectorizedData, getIndexes, currentFilter]);

  const reduceResources = (
    resourceRecord: App.ResourceElement[],
    currentFilter: App.CurrentFilter
  ) => {
    return resourceRecord
      .filter(
        (resource) =>
          resource.available ||
          resource.covid ||
          resource.nonCovid ||
          resource.total
      )
      .reduce((acc: App.ResourceElement[], currentResource) => {
        const existingRecord = acc.find(
          (record) => record.date === currentResource.date
        );
        if (existingRecord) {
          existingRecord.available += currentResource.available;
          existingRecord.covid += currentResource.covid;
          existingRecord.nonCovid += currentResource.nonCovid;
          existingRecord.total += currentResource.total;
        } else {
          acc.push(currentResource);
        }
        return acc;
      }, [])
      .map((resource) => {
        const available =
          resource.available /
          (currentFilter.filterType === "county"
            ? currentFilter.filters.length
            : 1);
        const covid =
          resource.covid /
          (currentFilter.filterType === "county"
            ? currentFilter.filters.length
            : 1);
        const nonCovid =
          resource.nonCovid /
          (currentFilter.filterType === "county"
            ? currentFilter.filters.length
            : 1);
        const total =
          resource.total /
          (currentFilter.filterType === "county"
            ? currentFilter.filters.length
            : 1);
        return {
          date: resource.date,
          available: +((available / total) * 100).toFixed(1),
          covid: +((covid / total) * 100).toFixed(1),
          nonCovid: +((nonCovid / total) * 100).toFixed(1),
          total: total,
        };
      })
      .sort(
        (resourceA, resourceB) =>
          new Date(resourceA.date).getTime() -
          new Date(resourceB.date).getTime()
      );
  };
  // since we are doing a hot swap of state values in the place of county-level values
  // then we need to take the length of the values and divide it by the total to get the
  // the state-wide number - I know not ideal, but with no API we need improv
  // todo cleanup
  const retainRegionalValues = (value: number, numberOfCounties: number) => {
    // setting denominator to 1 avoids infinity issues
    return value / (numberOfCounties > 0 ? numberOfCounties : 1);
  };

  const countyData: App.CountyStats[] = useMemo(() => {
    if (!vectorizedData) return [];

    const dailyObject = vectorizedData.metrics.daily_statistics;
    const indexCount = dailyObject.district.length;
    const weeklyObject = vectorizedData.metrics.weekly_statistics;
    const weeklyIndex = weeklyObject.district.length;
    const dailyStats = objectifyData<
      Data.DailyStatisticsVectors,
      Data.DailyStatisticsVector[]
    >(
      dailyObject,
      Array.from(Array(indexCount), (v, i) => i)
    );

    const weeklyStats = objectifyData<
      Data.WeeklyStatistics,
      Data.WeeklyStatistic[]
    >(
      weeklyObject,
      Array.from(Array(weeklyIndex), (v, i) => i)
    );

    return dailyStats.map((record) => {
      const weeklyObject = weeklyStats.find(
        (stat) => stat.district === record.district
      );
      return {
        id: record.district,
        name: record.county,
        region: record.isdh_region,
        population: record.population,
        type: record.district_type,
        cases: record.covid_cases,
        deaths: record.covid_deaths,
        positivityRate: record.m3b_covid_positive_tests_rate_moving_mean,
        positivityRateAdmin: record.m3b_covid_positive_test_valid_moving_mean,
        deathsProb: record.covid_deaths_prob,
        tests: record.covid_tests,
        testsAdmin: record.covid_tests_adm,
        newTestsAdmin: record.daily_delta_tests_adm,
        testsPositiveAdmin: 0,
        newCases: record.daily_delta_cases,
        newDeaths: record.daily_delta_deaths,
        newTests: record.daily_delta_tests,
        weeklyScore: weeklyObject?.weekly_score_week ?? 0,
        weeklyPerCapita: weeklyObject?.per_capita ?? 0,
        weeklyPositivityRate:
          weeklyObject && weeklyObject.positivity_rate_week
            ? +weeklyObject.positivity_rate_week.toFixed(2)
            : 0,
        weeklyPerCapitaScore: weeklyObject?.per_capita_score ?? 0,
        weeklyPositivityScore: weeklyObject?.positivity_rate_score_week ?? 0,
        weeklyPositivityRateDelta: weeklyObject?.positivity_rate_delta ?? 0,
        advisoryScore: weeklyObject ? weeklyObject.advisory_score_week - 1 : 0,
        newCasesStartEnd: record.daily_delta_cases_range as [
          string | null,
          string | null
        ],
        newDeathsStartEnd: record.daily_delta_deaths_range as [
          string | null,
          string | null
        ],
        newTestsStartEnd: record.daily_delta_tests_adm_range as [
          string | null,
          string | null
        ],
      };
    });
  }, [vectorizedData]);

  const demoData = useMemo(() => {
    const demoObject = {
      ageCases: [],
      ageDeaths: [],
      ageTests: [],
      genderCases: [],
      genderDeaths: [],
      genderTests: [],
      raceCases: [],
      raceDeaths: [],
      raceTests: [],
      ethnicityCases: [],
      ethnicityDeaths: [],
      ethnicityTests: [],
    };
    if (!vectorizedData) return demoObject;

    const dailyObject = vectorizedData.metrics.demographics;
    const indexCount = dailyObject.district.length;
    const objectedData = objectifyData<
      Data.DemographicsVectors,
      Data.DemographicsVector[]
    >(
      dailyObject,
      Array.from(Array(indexCount), (v, i) => i)
    );

    let filterIds: string[] = [];
    if (currentFilter.filterType === "state") {
      filterIds = ["0"];
    } else {
      filterIds = currentFilter.filters.map((filter) => filter.id);
    }

    const racePopulation = Object.keys(
      vectorizedData.metrics.race_population.RACE
    ).map((key, index) => ({
      name: vectorizedData.metrics.race_population.RACE[index],
      value: vectorizedData.metrics.race_population.POPULATION_PCT[index],
    }));

    const ethnicityPopulation = Object.keys(
      vectorizedData.metrics.ethn_population
    ).map((key, index) => ({
      name: vectorizedData.metrics.ethn_population.ETHNICITY[index],
      value: vectorizedData.metrics.ethn_population.POPULATION_PCT[index],
    }));

    function joinRecords(
      demoItems: { name: string; value: number }[],
      populationBinding: { name: string; value: number }[] = []
    ) {
      type DemoItem = {
        items: {
          name: string;
          value: number;
        }[];
        total: number;
      };

      const joined = demoItems.reduce(
        (acc: DemoItem, record) => {
          const existingRecord = acc.items.find(
            (existing) => existing.name === record.name
          );
          if (existingRecord) {
            existingRecord.value += record.value;
          } else {
            acc.items.push(record);
          }
          acc.total += record.value;
          return acc;
        },
        {
          items: [],
          total: 0,
        }
      );

      return joined.items.map((record) => ({
        name: record.name,
        value: !Number.isNaN(record.value / joined.total)
          ? (record.value / joined.total) * 100
          : 0,
        population: populationBinding.length
          ? populationBinding.find(
              (pop) => pop.name.toLowerCase() === record.name.toLowerCase()
            )?.value ?? 0
          : undefined,
      }));
    }

    return objectedData
      .filter((county) => filterIds.includes(county.district))
      .map((record) => ({
        ageCases: [
          {
            name: "0 to <1",
            value: record["m1d_agegrp_covid_cases_0 to <1"],
          },
          {
            name: "1-4",
            value: record["m1d_agegrp_covid_cases_1-4"],
          },
          {
            name: "5-11",
            value: record["m1d_agegrp_covid_cases_5-11"],
          },
          {
            name: "12-17",
            value: record["m1d_agegrp_covid_cases_12-17"],
          },
          {
            name: "18-19",
            value: record["m1d_agegrp_covid_cases_18-19"],
          },
          {
            name: "20-29",
            value: record["m1d_agegrp_covid_cases_20-29"],
          },
          {
            name: "30-39",
            value: record["m1d_agegrp_covid_cases_30-39"],
          },
          {
            name: "40-49",
            value: record["m1d_agegrp_covid_cases_40-49"],
          },
          {
            name: "50-59",
            value: record["m1d_agegrp_covid_cases_50-59"],
          },
          {
            name: "60-69",
            value: record["m1d_agegrp_covid_cases_60-69"],
          },
          {
            name: "70-79",
            value: record["m1d_agegrp_covid_cases_70-79"],
          },
          {
            name: "80+",
            value: record["m1d_agegrp_covid_cases_80+"],
          },
          {
            name: "Unknown",
            value: record.m1d_agegrp_covid_cases_unknown,
          },
        ],
        ageDeaths: [
          {
            name: "0-19",
            value: record["m1d_agegrp_covid_deaths_0-19"],
          },
          {
            name: "20-29",
            value: record["m1d_agegrp_covid_deaths_20-29"],
          },
          {
            name: "30-39",
            value: record["m1d_agegrp_covid_deaths_30-39"],
          },
          {
            name: "40-49",
            value: record["m1d_agegrp_covid_deaths_40-49"],
          },
          {
            name: "50-59",
            value: record["m1d_agegrp_covid_deaths_50-59"],
          },
          {
            name: "60-69",
            value: record["m1d_agegrp_covid_deaths_60-69"],
          },
          {
            name: "70-79",
            value: record["m1d_agegrp_covid_deaths_70-79"],
          },
          {
            name: "80+",
            value: record["m1d_agegrp_covid_deaths_80+"],
          },
          {
            name: "Unknown",
            value: record.m1d_agegrp_covid_deaths_unknown,
          },
        ],
        ageTests: [
          {
            name: "0-19",
            value: record["m1d_agegrp_covid_tests_0-19"],
          },
          {
            name: "20-29",
            value: record["m1d_agegrp_covid_tests_20-29"],
          },
          {
            name: "30-39",
            value: record["m1d_agegrp_covid_tests_30-39"],
          },
          {
            name: "40-49",
            value: record["m1d_agegrp_covid_tests_40-49"],
          },
          {
            name: "50-59",
            value: record["m1d_agegrp_covid_tests_50-59"],
          },
          {
            name: "60-69",
            value: record["m1d_agegrp_covid_tests_60-69"],
          },
          {
            name: "70-79",
            value: record["m1d_agegrp_covid_tests_70-79"],
          },
          {
            name: "80+",
            value: record["m1d_agegrp_covid_tests_80+"],
          },
          {
            name: "Unknown",
            value: record.m1d_agegrp_covid_tests_unknown,
          },
        ],
        genderCases: [
          {
            name: "Female",
            value: record.m1d_gender_covid_cases_f,
          },
          {
            name: "Male",
            value: record.m1d_gender_covid_cases_m,
          },
          {
            name: "Unknown",
            value: record.m1d_gender_covid_cases_unknown,
          },
        ],
        genderDeaths: [
          {
            name: "Female",
            value: record.m1d_gender_covid_deaths_f,
          },
          {
            name: "Male",
            value: record.m1d_gender_covid_deaths_m,
          },
          {
            name: "Unknown",
            value: record.m1d_gender_covid_deaths_unknown,
          },
        ],
        genderTests: [
          {
            name: "Female",
            value: record.m1d_gender_covid_tests_f,
          },
          {
            name: "Male",
            value: record.m1d_gender_covid_tests_m,
          },
          {
            name: "Unknown",
            value: record.m1d_gender_covid_tests_unknown,
          },
        ],
        raceCases: [
          {
            name: "White",
            value: record.m1d_race_covid_cases_white,
          },
          {
            name: "Other Race",
            value: record["m1d_race_covid_cases_other race"],
          },
          {
            name: "Black or African American",
            value: record["m1d_race_covid_cases_black or african american"],
          },
          {
            name: "Asian",
            value: record.m1d_race_covid_cases_asian,
          },
          {
            name: "Unknown",
            value: record.m1d_race_covid_cases_unknown,
          },
        ],
        raceDeaths: [
          {
            name: "White",
            value: record.m1d_race_covid_deaths_white,
          },
          {
            name: "Black or African American",
            value: record["m1d_race_covid_deaths_black or african american"],
          },
          {
            name: "Other Race",
            value: record["m1d_race_covid_deaths_other race"],
          },
          {
            name: "Asian",
            value: record.m1d_race_covid_deaths_asian,
          },
          {
            name: "Unknown",
            value: record.m1d_race_covid_deaths_unknown,
          },
        ],
        raceTests: [
          {
            name: "White",
            value: record.m1d_race_covid_tests_white,
          },
          {
            name: "Other Race",
            value: record["m1d_race_covid_tests_other race"],
          },
          {
            name: "Black or African American",
            value: record["m1d_race_covid_tests_black or african american"],
          },
          {
            name: "Asian",
            value: record.m1d_race_covid_tests_asian,
          },
          {
            name: "Unknown",
            value: record.m1d_race_covid_tests_unknown,
          },
        ],
        ethnicityCases: [
          {
            name: "Not Hispanic or Latino",
            value: record["m1d_ethnicity_covid_cases_not hispanic or latino"],
          },
          {
            name: "Hispanic or Latino",
            value: record["m1d_ethnicity_covid_cases_hispanic or latino"],
          },
          {
            name: "Unknown",
            value: record.m1d_ethnicity_covid_cases_unknown,
          },
        ],
        ethnicityDeaths: [
          {
            name: "Not Hispanic or Latino",
            value: record["m1d_ethnicity_covid_deaths_not hispanic or latino"],
          },
          {
            name: "Hispanic or Latino",
            value: record["m1d_ethnicity_covid_deaths_hispanic or latino"],
          },
          {
            name: "Unknown",
            value: record.m1d_ethnicity_covid_deaths_unknown,
          },
        ],
        ethnicityTests: [
          {
            name: "Not Hispanic or Latino",
            value: record["m1d_ethnicity_covid_tests_not hispanic or latino"],
          },
          {
            name: "Hispanic or Latino",
            value: record["m1d_ethnicity_covid_tests_hispanic or latino"],
          },
          {
            name: "Unknown",
            value: record.m1d_ethnicity_covid_tests_unknown,
          },
        ],
      }))
      .reduce((acc, record) => {
        // merge arrays
        acc.ageCases = joinRecords([...acc.ageCases, ...record.ageCases]);
        acc.ageDeaths = joinRecords([...acc.ageDeaths, ...record.ageDeaths]);
        acc.ageTests = joinRecords([...acc.ageTests, ...record.ageTests]);
        acc.genderCases = joinRecords([
          ...acc.genderCases,
          ...record.genderCases,
        ]);
        acc.genderDeaths = joinRecords([
          ...acc.genderDeaths,
          ...record.genderDeaths,
        ]);
        acc.genderTests = joinRecords([
          ...acc.genderTests,
          ...record.genderTests,
        ]);
        acc.raceCases = joinRecords(
          [...acc.raceCases, ...record.raceCases],
          racePopulation
        );
        acc.raceDeaths = joinRecords(
          [...acc.raceDeaths, ...record.raceDeaths],
          racePopulation
        );
        acc.raceTests = joinRecords(
          [...acc.raceTests, ...record.raceTests],
          racePopulation
        );
        acc.ethnicityCases = joinRecords(
          [...acc.ethnicityCases, ...record.ethnicityCases],
          ethnicityPopulation
        );
        acc.ethnicityDeaths = joinRecords(
          [...acc.ethnicityDeaths, ...record.ethnicityDeaths],
          ethnicityPopulation
        );
        acc.ethnicityTests = joinRecords(
          [...acc.ethnicityTests, ...record.ethnicityTests],
          ethnicityPopulation
        );
        return acc;
      }, demoObject);
  }, [vectorizedData, currentFilter]);

  const newAggregateMetrics = useCallback(
    (allCountyData: App.CountyStats[]) => {
      let filterIds: string[] = [];
      if (currentFilter.filterType === "state") {
        filterIds = ["0"];
      } else {
        filterIds = currentFilter.filters.map((filter) => filter.id);
      }
      function earlyAndLate(
        existingDate: string | null,
        newDate: string | null,
        earliest = true
      ) {
        if (!existingDate) return newDate ?? "";
        if (!newDate) return existingDate ?? "";
        return new Date(existingDate) > new Date(newDate)
          ? earliest
            ? newDate
            : existingDate
          : earliest
          ? existingDate
          : newDate;
      }
      return allCountyData
        .filter((record) => filterIds.includes(record.id))
        .reduce(
          (
            acc: Omit<
              App.Agg,
              | "cases"
              | "deaths"
              | "tests"
              | "deathsProb"
              | "testsAdmin"
              | "cumTestAdmin"
              | "testsPositiveAdmin"
              | "movingAverage"
              | "allMovingAverage"
              | "movingStart"
              | "movingEnd"
              | "casesStartEnd"
              | "deathsStartEnd"
              | "testsStartEnd"
              | "deathMoving"
              | "deathMovingStart"
              | "deathMovingEnd"
              | "weeklyPerCapita"
              | "weeklyPerCapitaScore"
              | "weeklyPositivityRate"
              | "weeklyPositivityScore"
              | "weeklyPositivityRateDelta"
              | "weeklyScore"
              | "advisoryScore"
            >,
            record
          ) => {
            return {
              newCases: acc.newCases + record.newCases,
              newDeaths: acc.newDeaths + record.newDeaths,
              newTests: acc.newTests + record.newTests,
              newTestsAdmin: acc.newTestsAdmin + record.newTestsAdmin,
              newCasesStartEnd: [
                earlyAndLate(
                  acc.newCasesStartEnd[0],
                  record.newCasesStartEnd[0]
                ),
                earlyAndLate(
                  acc.newCasesStartEnd[1],
                  record.newCasesStartEnd[1],
                  false
                ),
              ] as [string, string],
              newDeathsStartEnd: [
                earlyAndLate(
                  acc.newDeathsStartEnd[0],
                  record.newDeathsStartEnd[0]
                ),
                earlyAndLate(
                  acc.newDeathsStartEnd[1],
                  record.newDeathsStartEnd[1],
                  false
                ),
              ] as [string, string],
              newTestsStartEnd: [
                earlyAndLate(
                  acc.newTestsStartEnd[0],
                  record.newTestsStartEnd[0]
                ),
                earlyAndLate(
                  acc.newTestsStartEnd[1],
                  record.newTestsStartEnd[1],
                  false
                ),
              ] as [string, string],
            };
          },
          {
            newCases: 0,
            newDeaths: 0,
            newTests: 0,
            newTestsAdmin: 0,
            newCasesStartEnd: ["", ""],
            newTestsStartEnd: ["", ""],
            newDeathsStartEnd: ["", ""],
          }
        );
    },
    [currentFilter]
  );

  const variantData = useMemo(() => {
    if (!vectorizedData) return [];

    const variantObject = vectorizedData.metrics.variants;
    return objectifyData<Data.Variants, App.Variant[]>(
      variantObject,
      Array.from(variantObject.origin, (_, i) => i)
    );
  }, [vectorizedData]);

  const aggregateReduce = useCallback(
    (timeSeries: App.DailyElement[], reportDate: string) => {
      // analysis reportDate versus end date in array
      const finalRecord = timeSeries.slice().pop();
      const distance = finalRecord
        ? (new Date(reportDate).getTime() -
            new Date(finalRecord.date).getTime()) /
          (1000 * 60 * 60 * 24)
        : 0;
      const movingAverageOffset =
        timeSeries.length - 1 - (distance > 6 ? 0 : 6 - distance);

      function fixDates(
        eventCountThatDay: number,
        date: string,
        currentValues: [string | null, string | null]
      ) {
        return [
          eventCountThatDay &&
          new Date(date).getTime() <
            (currentValues[0]
              ? new Date(currentValues[0])
              : new Date(date).getTime() + 1)
            ? date
            : currentValues[0],
          eventCountThatDay &&
          new Date(date).getTime() >
            (currentValues[1]
              ? new Date(currentValues[1])
              : new Date(date).getTime() - 1)
            ? date
            : currentValues[1],
        ];
      }
      return timeSeries.reduce(
        (
          acc: Omit<
            App.Agg,
            | "newCases"
            | "newCasesStartEnd"
            | "newDeaths"
            | "newDeathsStartEnd"
            | "newTests"
            | "newTestsStartEnd"
            | "newTestsAdmin"
            | "weeklyPerCapita"
            | "weeklyPerCapitaScore"
            | "weeklyPositivityRate"
            | "weeklyPositivityScore"
            | "weeklyPositivityRateDelta"
            | "weeklyScore"
            | "advisoryScore"
          >,
          current,
          index
        ) => {
          acc.cases += current.existingCases + current.newCases;
          acc.casesStartEnd = fixDates(
            current.existingCases,
            current.date,
            acc.casesStartEnd
          ) as [string, string];
          acc.deaths += current.existingDeaths + current.newDeaths;
          acc.deathsStartEnd = fixDates(
            current.existingDeaths,
            current.date,
            acc.deathsStartEnd
          ) as [string, string];
          acc.tests += current.existingTests + current.newTests;
          acc.testsAdmin += current.existingTestAdmin + current.newTestAdmin;
          acc.testsStartEnd = fixDates(
            current.existingTestAdmin,
            current.date,
            acc.testsStartEnd
          ) as [string, string];
          acc.testsPositiveAdmin += current.testPositiveAdminLag;
          acc.cumTestAdmin += current.testAdminLag;
          if (index === timeSeries.length - 7) {
            acc.deathMovingStart = current.date;
          }
          if (index === timeSeries.length - 1) {
            acc.deathMoving = current.deathMoving;
            acc.deathMovingEnd = current.date;
          }
          if (index === movingAverageOffset - 6) {
            acc.movingStart = current.date;
          }
          if (index === movingAverageOffset) {
            acc.movingEnd = current.date;
            acc.movingAverage = current.testRateMoving;
            acc.allMovingAverage = current.testAdminRateMoving;
          }
          return acc;
        },
        {
          cases: 0,
          deaths: 0,
          deathsProb: 0,
          tests: 0,
          testsAdmin: 0,
          cumTestAdmin: 0,
          testsPositiveAdmin: 0,
          movingAverage: 0,
          allMovingAverage: 0,
          deathMoving: 0,
          movingStart: "",
          movingEnd: "",
          deathMovingStart: "",
          deathMovingEnd: "",
          casesStartEnd: [null, null],
          deathsStartEnd: [null, null],
          testsStartEnd: [null, null],
        }
      );
    },
    []
  );

  useEffect(() => {
    if (!vectorizedData) return;
    const recordData = seriesFromVector;
    if (!recordData) return;

    let dateRange: [number, number] = [0, 0];

    if (dateFilter) {
      dateRange = dateFilter;
    } else {
      const totalDays = vectorizedData.metrics.data.date.length + 1;
      const startDate = new Date();
      const endDate = new Date(vectorizedData.report_date);
      dateRange = [
        startDate.setDate(endDate.getDate() - totalDays),
        endDate.getTime(),
      ];
    }

    const filterTimeSeries = recordData.filter((record) => {
      const dateInMilli = new Date(record.date).getTime();
      return dateInMilli <= dateRange[1] && dateInMilli >= dateRange[0];
    });

    const icuData = reduceResources(
      filterTimeSeries.map((record) => ({
        date: record.date,
        covid: record.m2b_hospitalized_icu_occupied_covid!,
        nonCovid: record.m2b_hospitalized_icu_occupied_non_covid!,
        available: record.m2b_hospitalized_icu_available!,
        total: record.m2b_hospitalized_icu_supply!,
      })),
      currentFilter
    );

    const ventData = reduceResources(
      filterTimeSeries.map((record) => ({
        date: record.date,
        covid: record.m2b_hospitalized_vent_occupied_covid!,
        nonCovid: record.m2b_hospitalized_vent_occupied_non_covid!,
        available: record.m2b_hospitalized_vent_available!,
        total: record.m2b_hospitalized_vent_supply!,
      })),
      currentFilter
    );

    // todo clean this up
    const hospitalizationCensus = filterTimeSeries
      .filter((record) => record.m1a_beds_all_occupied_beds_covid_19_smoothed)
      .map((record) => ({
        date: record.date,
        value: record.m1a_beds_all_occupied_beds_covid_19_smoothed!,
        pui: record.m1a_beds_all_occupied_beds_covid_19_pui_smoothed,
        pts: record.m1a_beds_all_occupied_beds_covid_19_pts_smoothed,
      }))
      .reduce((acc: App.HospitalCensusRecord[], currentRecord) => {
        const existingRecord = acc.find(
          (record) => record.date === currentRecord.date
        );
        if (existingRecord) {
          existingRecord.value += currentRecord.value;
          existingRecord.pui += currentRecord.pui;
          existingRecord.pts += currentRecord.pts;
        } else {
          acc.push(currentRecord);
        }
        return acc;
      }, [])
      .map((record) => ({
        ...record,
        value: Math.round(
          retainRegionalValues(
            record.value,
            currentFilter.filterType === "county"
              ? currentFilter.filters.length
              : 1
          )
        ),
        pui: Math.round(
          retainRegionalValues(
            record.pui,
            currentFilter.filterType === "county"
              ? currentFilter.filters.length
              : 1
          )
        ),
        pts: Math.round(
          retainRegionalValues(
            record.pts,
            currentFilter.filterType === "county"
              ? currentFilter.filters.length
              : 1
          )
        ),
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // todo clean this up
    const hospitalizationAdmissions = filterTimeSeries
      .map((record) => ({
        date: record.date,
        value: record.m1c_covid_cases_admission_rolling_sum!,
        pui: record.m1a_beds_all_occupied_beds_covid_19_pui_smoothed,
        pts: record.m1a_beds_all_occupied_beds_covid_19_pts_smoothed,
      }))
      .reduce((acc: App.HospitalRecord[], currentRecord) => {
        const existingRecord = acc.find(
          (record) => record.date === currentRecord.date
        );
        if (existingRecord) {
          existingRecord.value += currentRecord.value;
        } else {
          acc.push(currentRecord);
        }
        return acc;
      }, [])
      // todo figure out why the date stuff requires this sorting function
      // while the test rates is just a simple date compare
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    let dailyMetrics = filterTimeSeries
      .reduce(
        (
          acc: Omit<
            App.DailyElement[],
            | "testMoving"
            | "testRateMoving"
            | "allTestMoving"
            | "allTestRateMoving"
          >,
          metric
        ) => {
          const existingRecord = acc.find(
            (record) => record.date === metric.date
          );
          if (existingRecord) {
            existingRecord.newCases += metric.m1e_daily_delta_cases;
            existingRecord.existingCases += metric.m1e_daily_base_cases;
            existingRecord.caseMoving += metric.m1e_covid_cases_moving_mean;
            existingRecord.newDeaths += metric.m1e_daily_delta_deaths;
            existingRecord.existingDeaths += metric.m1e_daily_base_deaths;
            existingRecord.deathRate += metric.m1e_covid_deaths;
            existingRecord.deathMoving += metric.m1e_covid_deaths_moving_mean;
            existingRecord.newTests += metric.m1e_daily_delta_tests;
            existingRecord.existingTests += metric.m1e_daily_base_tests;
            existingRecord.testMoving += metric.m1e_covid_tests_moving_mean;
            existingRecord.testLag += metric.m3b_covid_tests_lag;
            existingRecord.positiveTestLag +=
              metric.m3b_covid_tests_positive_lag;
            existingRecord.testAdminLag += metric.m3b_covid_tests_valid_lag;
            existingRecord.testPositiveAdminLag +=
              metric.m3b_covid_positive_tests_valid_lag;
            existingRecord.testAdminMoving +=
              metric.m1e_covid_tests_adm_moving_mean;
            existingRecord.existingTestAdmin += metric.m1e_daily_base_tests_adm;
            existingRecord.newTestAdmin += metric.m1e_daily_delta_tests_adm;
          } else {
            acc.push({
              date: metric.date,
              newDeaths: metric.m1e_daily_delta_deaths,
              newCases: metric.m1e_daily_delta_cases,
              existingCases: metric.m1e_daily_base_cases,
              caseMoving: metric.m1e_covid_cases_moving_mean,
              existingDeaths: metric.m1e_daily_base_deaths,
              deathRate: metric.m1e_covid_deaths,
              testRate: 0,
              testRateMoving: 0,
              deathMoving: metric.m1e_covid_deaths_moving_mean,
              newTests: metric.m1e_daily_delta_tests,
              existingTests: metric.m1e_daily_base_tests,
              testAdminRate: 0,
              testAdminRateMoving: 0,
              testAdminMoving: metric.m1e_covid_tests_adm_moving_mean,
              existingTestAdmin: metric.m1e_daily_base_tests_adm,
              newTestAdmin: metric.m1e_daily_delta_tests_adm,
              testMoving: metric.m1e_covid_tests_moving_mean,
              testLag: metric.m3b_covid_tests_lag,
              positiveTestLag: metric.m3b_covid_tests_positive_lag,
              testAdminLag: metric.m3b_covid_tests_valid_lag,
              testPositiveAdminLag: metric.m3b_covid_positive_tests_valid_lag,
            });
          }
          return acc;
        },
        []
      )
      .sort((a, b) => (new Date(a.date) > new Date(b.date) ? 1 : 0));

    const movingAvg = movingSum(
      dailyMetrics.map((metric) =>
        Number.isNaN(metric.positiveTestLag / metric.testLag)
          ? [null, null]
          : [metric.positiveTestLag, metric.testLag]
      )
    );

    const allTestSumTotal = movingSum(
      dailyMetrics.map((metric) =>
        Number.isNaN(metric.testPositiveAdminLag / metric.testAdminLag)
          ? [null, null]
          : [metric.testPositiveAdminLag, metric.testAdminLag]
      )
    );

    dailyMetrics = dailyMetrics.map((record, index) => ({
      ...record,
      testRate: makeWhole(record.positiveTestLag / record.testLag),
      testRateMoving: makeWhole(movingAvg[index]),
      testAdminRate: makeWhole(
        record.testPositiveAdminLag / record.testAdminLag
      ),
      testAdminRateMoving: makeWhole(allTestSumTotal[index]),
    }));

    setReportDate({
      raw: vectorizedData.timestamp,
      formatted: formatReportDate(vectorizedData.timestamp),
    });

    // todo clean-up naming
    const aggregate = aggregateReduce(dailyMetrics, vectorizedData.report_date);
    const newStats = newAggregateMetrics(countyData);

    function insideWindow(date: string, reportDate: string, value: number) {
      return new Date(date) >= new Date(refAreaCalc(reportDate)) ? null : value;
    }

    if (currentFilter.filterType === "state") {
      aggregate.reinfections = JSON.parse(
        vectorizedData.metrics.reinfections_cases
      ).count["0"];
    }

    setData({
      agg: { ...aggregate, ...newStats },
      daily: dailyMetrics.map((record) => ({
        ...record,
        testRateMoving: insideWindow(
          record.date,
          vectorizedData.report_date,
          record.testRateMoving
        ),
        testMoving: insideWindow(
          record.date,
          vectorizedData.report_date,
          record.testMoving
        ),
        testAdminMoving: insideWindow(
          record.date,
          vectorizedData.report_date,
          record.testAdminMoving
        ),
        testAdminRateMoving: insideWindow(
          record.date,
          vectorizedData.report_date,
          record.testAdminRateMoving
        ),
      })),
      map: countyData.filter((record) => record.type === "c"),
      demo: {
        ...demoData,
        suppressDeath: aggregate.deaths < 10 ? true : false,
      },
      bedvent: {
        beds: icuData,
        vents: ventData,
      },
      hospital: {
        census: hospitalizationCensus,
        admissions: hospitalizationAdmissions,
      },
      probable: {
        probableDeaths:
          countyData.find((county) => county.id === "0")?.deathsProb ?? 0,
      },
      variants: variantData,
      variantsByWeek: vectorizedData.metrics.variants_by_week,
      misc: vectorizedData.metrics.misc,
    });
  }, [
    countyData,
    aggregateReduce,
    newAggregateMetrics,
    seriesFromVector,
    vectorizedData,
    currentFilter,
    demoData,
    dateFilter,
    variantData,
  ]);

  return { data, reportDate };
}
